import { format, isValid, parse } from 'date-fns'
import type { InputHTMLAttributes, ReactNode } from 'react'
import React, { forwardRef, useCallback } from 'react'
import styled, { css } from 'styled-components'
import ErrorIcon from '../../../components/icons/ErrorIcon'
import { textStyle } from '../../helpers'
import { Typography } from '../Typography'

interface DateInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type' | 'min' | 'max'
> {
  label?: string
  error?: string | boolean
  helperText?: string
  startAdornment?: ReactNode
  endAdornment?: ReactNode
  fullWidth?: boolean
  value: Date | null
  onChange: (date: Date | null) => void
  min?: Date
  max?: Date
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      label,
      error,
      helperText,
      startAdornment,
      endAdornment,
      fullWidth = false,
      required,
      id,
      value,
      onChange,
      min,
      max,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId()
    const inputId = id || generatedId
    const hasError = !!error
    const errorMessage = typeof error === 'string' ? error : ''
    const displayMessage = hasError ? errorMessage : helperText
    const dateInputRef = React.useRef<HTMLInputElement>(null)

    const dateToString = useCallback((date: Date | null): string => {
      if (!date || !isValid(date)) return ''
      return format(date, 'yyyy-MM-dd')
    }, [])

    const dateToDisplayString = useCallback((date: Date | null): string => {
      if (!date || !isValid(date)) return ''
      return format(date, 'yyyy/MM/dd')
    }, [])

    const stringToDate = useCallback((dateString: string): Date | null => {
      if (!dateString) return null
      const parsed = parse(dateString, 'yyyy-MM-dd', new Date())
      return isValid(parsed) ? parsed : null
    }, [])

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const dateValue = e.target.value
        onChange(stringToDate(dateValue))
      },
      [onChange, stringToDate]
    )

    const handleDisplayClick = useCallback(() => {
      // disabled 상태에서는 클릭 무시
      if (props.disabled) return

      // 스타일용 input 클릭 시 실제 date input을 focus (사파리 호환)
      if (dateInputRef.current) {
        dateInputRef.current.focus()
        try {
          dateInputRef.current.showPicker?.()
        } catch {
          // showPicker를 지원하지 않는 브라우저에서는 무시
        }
      }
    }, [props.disabled])

    return (
      <Container $fullWidth={fullWidth}>
        {label && (
          <Label htmlFor={inputId}>
            <Typography variant="Heading/Heading-14-Semibold" color="label">
              {label}
              {required && <RequiredMark>*</RequiredMark>}
            </Typography>
          </Label>
        )}
        <InputWrapper $hasError={hasError} $disabled={!!props.disabled}>
          {startAdornment && <Adornment>{startAdornment}</Adornment>}
          {/* 스타일용 input (보이는 부분) */}
          <DisplayInput
            type="text"
            value={dateToDisplayString(value)}
            placeholder="YYYY/MM/DD"
            onClick={handleDisplayClick}
            disabled={props.disabled}
            readOnly
            aria-invalid={hasError}
            aria-describedby={displayMessage ? `${inputId}-helper` : undefined}
          />
          {/* 실제 date input (숨겨진 부분) */}
          <HiddenDateInput
            ref={node => {
              dateInputRef.current = node
              if (typeof ref === 'function') {
                ref(node)
              } else if (ref) {
                ref.current = node
              }
            }}
            id={inputId}
            type="date"
            value={dateToString(value)}
            onChange={handleChange}
            min={min ? dateToString(min) : undefined}
            max={max ? dateToString(max) : undefined}
            required={required}
            disabled={props.disabled}
            tabIndex={-1}
            {...props}
          />
          {endAdornment && <Adornment>{endAdornment}</Adornment>}
        </InputWrapper>
        {displayMessage && (
          <HelperTextWrapper id={`${inputId}-helper`}>
            <ErrorIcon />
            <Typography
              variant="Heading/Heading-14-medium"
              color={hasError ? 'danger' : 'textSecondary'}
            >
              {displayMessage}
            </Typography>
          </HelperTextWrapper>
        )}
      </Container>
    )
  }
)

DateInput.displayName = 'DateInput'

const Container = styled.div<{ $fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  gap: 0.5rem;
`

const Label = styled.label`
  display: block;
`

const RequiredMark = styled.span`
  color: ${({ theme }) => theme.colors.danger};
`

const InputWrapper = styled.div<{ $hasError: boolean; $disabled: boolean }>`
  position: relative;
  padding: 1.125rem 1rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.border};
  border-radius: 0.5rem;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  ${({ $hasError, theme }) =>
    $hasError &&
    css`
      border-color: ${theme.colors.danger};
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      background-color: ${({ theme }) => theme.palette.gray[50]};
      cursor: not-allowed;
    `}

  &:hover:not(:focus-within) {
    ${({ $hasError, $disabled, theme }) =>
      !$disabled &&
      !$hasError &&
      css`
        border-color: ${theme.colors.textSecondary};
      `}
  }
`

const DisplayInput = styled.input`
  display: flex;
  width: 100%;
  padding: 0;
  background: transparent;
  border: none;
  outline: none;
  color: ${({ theme }) => theme.colors.text};
  ${() => textStyle('Button/Button-16-medium')}

  &::placeholder {
    color: ${({ theme }) => theme.colors.placeholder};
    opacity: 1;
  }

  &:not(:disabled) {
    cursor: pointer;
  }

  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`

const HiddenDateInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: -1;
`

const Adornment = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  flex-shrink: 0;
`

const HelperTextWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.25rem;
`
